import React, { useState, useEffect, FC, useMemo, useRef } from 'react';
import './other-table.scss';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import OtherTableRow, { typeIdToLabel } from '../other-table-row/other-table-row';
import { useSelector, useDispatch } from 'react-redux';
import { Chip } from 'primereact/chip';
import StockTableCountCell, { StockDestination } from '../stock-table-count-cell/stock-table-count-cell';
import { updateOPProductsByShop } from '../../productSlices';

export enum ProductType {
    FOOD = 'Food',
    OTHER = 'Other'
}

// Add these types at the top
type SortField = 'name' | 'type' | 'total_quantity' | 'pack_count' | 'pack_size';
type SortOrder = 'asc' | 'desc';

const OtherTable = ({ 
  showUpperControls = true,
  search,
  setSearch,
  showFilters,
  setShowFilters,
  selectedFilters,
  setSelectedFilters,
  handleStockModify,
  modifiedProducts,
  hasModifications,
  onShowModifications,
  ...props 
}) => {
    const op_products = useSelector((state: any) => state.product.op_products);
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const user = useSelector((state: any) => state.auth.user);
    const userAllowedToEdit = user.roleLevel >= 2;
    const bulkLoading = useSelector((state: any) => state.product.bulk_loading);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 7;
    const [checkedStocks, setCheckedStocks] = useState([]);
    const [destinationShop, setDestinationShop] = useState<string | null>(null);  
    // Add sorting state
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    // Add mobile state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Add resize handler
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterContainerRef.current && !filterContainerRef.current.contains(event.target as Node)) {
                setShowFilters(false);
            }
        };

        if (showFilters) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilters, setShowFilters]);

    // Update filteredStock to include sorting
    const filteredStock = useMemo(() => {
        const filtered = op_products.filter(stock => {
            const matchesSearch = stock.name.toLowerCase().includes(search.toLowerCase());
            const matchesFilters = selectedFilters.length === 0 || selectedFilters.includes(stock.name);
            return matchesSearch && matchesFilters;
        });

        return [...filtered].sort((a, b) => {
            let comparison = 0;
            switch (sortField) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'type':
                    comparison = a.type.localeCompare(b.type);
                    break;
                case 'total_quantity':
                    comparison = parseFloat(a.total_quantity || '0') - parseFloat(b.total_quantity || '0');
                    break;
                case 'pack_count':
                    comparison = parseFloat(a.pack_count || '0') - parseFloat(b.pack_count || '0');
                    break;
                case 'pack_size':
                    comparison = parseFloat(a.pack_size || '0') - parseFloat(b.pack_size || '0');
                    break;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [op_products, search, selectedFilters, sortField, sortOrder]);

    // Add sort handler
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const handleFilterToggle = (productName: string) => {
        setSelectedFilters(prev => 
            prev.includes(productName) 
                ? prev.filter(name => name !== productName)
                : [...prev, productName]
        );
    };

    const handleSelectAllFilters = (checked: boolean) => {
        setSelectedFilters(checked ? uniqueProductNames : []);
    };

    const uniqueProductNames = useMemo(() => {
        return Array.from(new Set(op_products.map(product => product.name))) as string[];
    }, [op_products]);

    const relevantStocks = useMemo(() => {
        return filteredStock.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    }, [filteredStock, currentPage]);

    const allChecked = checkedStocks.length === relevantStocks.length && relevantStocks.length > 0;
    const setAllChecked = (checked: boolean) => {
        if(checked)
            setCheckedStocks(relevantStocks.map(stock => stock.id));
        else
            setCheckedStocks([]);
    }
    const setChecked = (checked: boolean, id: string) => {
        if(checked)
            setCheckedStocks([...checkedStocks, id]);
        else
            setCheckedStocks(checkedStocks.filter(stockId => stockId !== id));
    }

    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, filteredStock.length);
    const visibleStocks = `${startIndex}-${endIndex > filteredStock.length ? filteredStock.length:endIndex} of ${filteredStock.length}`;

    const handlePageChange = (page: number) => {
        if(page < 1)
            setCurrentPage(1);
        else
            setCurrentPage(page);
    }

    const MobileStockItem = ({ stock, checked, setChecked }) => {
        const originalStock = op_products.find(s => s.id === stock.id);
        return (
            <div className="other-table__table__mobile-item">
                <div className="other-table__table__mobile-item__info">
                    <div className="other-table__table__mobile-item__info__name">
                        {stock.name}
                    </div>
                    <div className="other-table__table__mobile-item__info__total">
                        Total: {stock.total_quantity}
                    </div>
                </div>
                <div className="other-table__table__mobile-item__cells">
                    <div className="other-table__table__mobile-item__cells__column">
                        <div className="other-table__table__mobile-item__cells__column__header">
                            TYPE
                        </div>
                        <Chip label={typeIdToLabel[stock.type]} className={typeIdToLabel[stock.type]} />
                    </div>
                    <div className="other-table__table__mobile-item__cells__column">
                        <div className="other-table__table__mobile-item__cells__column__header">
                            TOTAL
                        </div>
                        <StockTableCountCell 
                            transfer={true} 
                            stock={stock.total_quantity}
                            setStock={(count, destinationShop, destination) => handleStockUpdate(stock.id, 'total_quantity', count, destinationShop, destination)}
                            handleStockModify={(count, destinationShop, destination) => handleStockModify(stock.id, 'total_quantity', count, destinationShop, destination)}
                            destinationShop={destinationShop}
                            setDestinationShop={setDestinationShop}
                            originalValue={originalStock.total_quantity}
                            externalDestination={stock.destination}
                        />
                    </div>
                    <div className="other-table__table__mobile-item__cells__column">
                        <div className="other-table__table__mobile-item__cells__column__header">
                            PACK COUNT
                        </div>
                        <StockTableCountCell 
                            transfer={true} 
                            stock={stock.pack_count} 
                            setStock={(count, destinationShop, destination) => handleStockUpdate(stock.id, 'pack_count', count, destinationShop, destination)}
                            handleStockModify={(count, destinationShop, destination) => handleStockModify(stock.id, 'pack_count', count, destinationShop, destination)}
                            destinationShop={destinationShop}
                            setDestinationShop={setDestinationShop}
                            originalValue={originalStock.pack_count}
                            externalDestination={stock.destination}
                        />
                    </div>
                    <div className="other-table__table__mobile-item__cells__column">
                        <div className="other-table__table__mobile-item__cells__column__header">
                            PACK SIZE
                        </div>
                        <div>{stock.pack_size}</div>
                    </div>
                </div>
            </div>
        );
    };

    const dispatch: Function = useDispatch();
    const handleStockUpdate = (id: string, field: string, value: number, destination_shop_id: string, destination: string) => {
        const stock = relevantStocks.find(s => s.id === id);
        const newStock = {
            ...stock,
            [field]: value,
            checked: undefined,
            setChecked: undefined,
            destination_shop_id: destination === StockDestination.OtherShop ? destination_shop_id : undefined,
            has_been_consumed: !(destination === StockDestination.Trash),
            destination: destination
        };
        dispatch(updateOPProductsByShop(selectedShop, id, newStock));
    };

    const selectedShop = useSelector((state: any) => state.user.selectedShop);

    return (
        <div className="other-table">
            {showUpperControls && (
                <div className="other-table__upper">
                    <div className="other-table__upper__controls">
                        <span className="p-inputgroup">
                            <span className="p-inputgroup-addon">
                                <i className="pi pi-search"></i>
                            </span>
                            <InputText 
                                placeholder="Search..." 
                                className="other-table__upper__controls__filter-button" 
                                onChange={(e) => setSearch(e.target.value)} 
                                value={search}
                            />
                        </span>
                        <div className="other-table__filter-container">
                            <Button 
                                onClick={() => setShowFilters(!showFilters)} 
                                icon="pi pi-filter" 
                                label={'Filters'} 
                                className={`other-table__upper__controls__filter-button ${showFilters ? 'p-button-highlighted' : ''} ${selectedFilters.length > 0 ? 'filters-active' : ''}`} 
                            />
                        </div>
                        <Button 
                            onClick={onShowModifications} 
                            icon={bulkLoading ? "pi pi-spinner pi-spin" : "pi pi-save"} 
                            iconPos='left' 
                            disabled={!hasModifications() || bulkLoading} 
                            label={'Update All'} 
                            className="other-table__upper__controls__save-button" 
                        />
                        {userAllowedToEdit && (
                            <Button 
                                onClick={() => props.setIsPopupOpen(true)} 
                                icon="pi pi-plus" 
                                iconPos='left' 
                                label={'Add products'} 
                                className="other-table__upper__controls__add-button" 
                            />
                        )}
                    </div>
                </div>
            )}
            <div className="other-table__filter-container">
                {showFilters && (
                    <div className="other-table__filters-popup" ref={filterContainerRef}>
                        <div className="other-table__filters-popup__item">
                            <Checkbox 
                                checked={selectedFilters.length === uniqueProductNames.length}
                                onChange={(e) => handleSelectAllFilters(e.checked)}
                                id="filter-select-all"
                            />
                            <label htmlFor="filter-select-all">Select All</label>
                        </div>
                        {uniqueProductNames.map(name => (
                            <div key={name} className="other-table__filters-popup__item">
                                <Checkbox 
                                    checked={selectedFilters.includes(name)}
                                    onChange={() => handleFilterToggle(name)}
                                    id={`filter-${name}`}
                                />
                                <label htmlFor={`filter-${name}`}>{name}</label>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="other-table__table">
                {!isMobile ? (
                    <>
                        <div className="other-table__table__header">
                            <Checkbox onChange={e => setAllChecked(e.checked)} checked={allChecked}></Checkbox>
                            <div 
                                onClick={() => handleSort('name')} 
                                className="sortable-header"
                            >
                                PRODUCTS {sortField === 'name' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('type')} 
                                className="sortable-header"
                            >
                                TYPE {sortField === 'type' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('total_quantity')} 
                                className="sortable-header"
                            >
                                TOTAL {sortField === 'total_quantity' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('pack_count')} 
                                className="sortable-header"
                            >
                                NUMBER OF PACKS {sortField === 'pack_count' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                            <div 
                                onClick={() => handleSort('pack_size')} 
                                className="sortable-header"
                            >
                                PACK SIZE {sortField === 'pack_size' && <i className={`pi pi-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>}
                            </div>
                        </div>
                        {relevantStocks.map((stock) => (
                            <OtherTableRow
                                key={stock.id}
                                {...stock}
                                checked={checkedStocks.includes(stock.id)}
                                setChecked={setChecked}
                                setProductIdToDelete={props.setProductIdToDelete}
                                setWordToConfirm={props.setWordToConfirm}
                                setProductToEdit={props.setProductToEdit}
                                handleStockModify={handleStockModify}
                            />
                        ))}
                    </>
                ) : (
                    modifiedProducts.map((stock) => (
                        <MobileStockItem
                            key={stock.id}
                            stock={stock}
                            checked={checkedStocks.includes(stock.id)}
                            setChecked={setChecked}
                            
                        />
                    ))
                )}
                {[...Array(Math.max(0, itemsPerPage - relevantStocks.length))].map((_, index) => (
                    <div key={`empty-${index}`} className="other-table__table__row--empty" />
                ))}
            </div>
            <div className="other-table__lower-controls">
                <div className="other-table__lower-controls__rows-per-page">{`Rows per page: ${itemsPerPage}`}</div>
                <div className="other-table__lower-controls__visible-rows">{visibleStocks}</div>
                <Button 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    icon="pi pi-arrow-left" 
                    label={'Previous'} 
                    className={`other-table__lower-controls__button ${currentPage===1 ? 'disabled' : ''}`} 
                />
                <Button 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    icon="pi pi-arrow-right" 
                    iconPos='right' 
                    label={'Next'} 
                    className={`other-table__lower-controls__button ${endIndex >= filteredStock.length ? 'disabled' : ''}`} 
                />
            </div>
        </div>
    );
}
         
export default OtherTable;